import { useCallback, useEffect, useRef, useState } from 'react';

import { services } from '@/services';
import { useFirestoreSubscription } from '@/services/firebase/useFirestoreSubscription';
import { useCheckBatchStore } from '@/stores/checkBatchStore';
import {
  mapCheckJobDoc,
  mapCheckJobItemsDocs,
  type CheckJobDocDto,
  type CheckJobItemDto,
} from '@/types/check.mappers';

/**
 * useCheckBatchListener — 批次檢查進度訂閱 glue(R209;R211 加 Firestore 分支;
 * **R212 依 docs/88 §2.3 改兩路訂閱**)。
 * 掛在 CheckBatchMinibar(AppShell 常駐)→ 全站生效、跨頁導航不斷線。
 * -----------------------------------------------------------------------------
 * **推送路徑(由資料決定誰生效,換實作時本檔零改動)**:
 *   1. `service.subscribeBatch` — fixture 佔位期的 timer 推送;http 版回 no-op
 *   2. **`firestore_paths.job`(doc)+ `firestore_paths.steps_collection`(collection)
 *      兩路 onSnapshot** — BE 上線後的即時進度
 *
 * ⚠️ **為什麼一定要兩路**:job doc 是**平鋪的**且**沒有 items**(§2.3)—— 逐件 badge
 * 只存在 items 子集合。R211 只訂 job 一路且用 REST 的兩層 mapper 去解,結果每次
 * job doc 推播都把 `results`/`order`/`currentKey` 覆蓋成空 = 進度條在動但一筆結果
 * 都不會寫回清單。現在 job doc 只更新計數/進度(`mapCheckJobDoc` 回 Partial),
 * items 子集合只更新 `results`/`reportIds`/`order`(`mapCheckJobItemsDocs`)。
 *
 * cold-start 兩段(R213):
 *   - store **有** batchId → `getBatch` 對帳,查無(fixture in-memory 遺失 / job 不存在)
 *     → 清 stale,兩實作同語意
 *   - store **沒有** batch → `listActiveBatches`(docs/88 §1.5)問伺服器有沒有還在跑的,
 *     蓋掉 persist 30 分 TTL / 換裝置 / 清 localStorage 這三種「job_id 弄丟了」
 *
 * **rules 未部署期**(docs/87「現在能做到哪裡」):job doc 的 onSnapshot 會回
 * `permission-denied`。此時標記 `listenerBlocked` 讓 UI 長出「手動更新進度」出口,
 * 並在**回到前景**時取一次快照補上 —— **不得退成 setInterval 輪詢**(§2.3 明禁)。
 */
export function useCheckBatchListener(): void {
  const batchId = useCheckBatchStore((s) => (s.batch?.state === 'run' ? s.batch.batchId : null));
  // path 由 cold-start / 推送結果帶出(BE 算好,FE 不自拼 — ADR-002)
  const [firestorePath, setFirestorePath] = useState('');
  const [itemsPath, setItemsPath] = useState('');
  /** 前景補抓的節流時戳(避免切分頁來回時連打)。 */
  const lastSnapAtRef = useRef(0);

  /**
   * **冷啟還原**(docs/88 §1.5 `GET /asset_check/jobs`)—— 掛載時問一次「我在這個工作區
   * 有沒有還在跑的批次」,不必先知道 `job_id`。
   *
   * 為什麼需要:進度來源只有 persist 的 `batch.batchId` 一條,而它有 **30 分鐘 TTL**
   * (`CHECK_BATCH_TTL_MS`)。12 件的批次本來就要 3–8 分鐘,一批大的跑過 TTL、或換一台
   * 裝置 / 換一個瀏覽器 / 清過 localStorage,job_id 就永遠找不回來 —— 進度條與清單的
   * 「檢查中」列全部消失,使用者只會以為沒跑到而**再按一次 = 再扣一次點**。
   *
   * 只在 store **完全沒有 batch** 時認養:已經有一筆(還在跑、或跑完還在顯示結果)就
   * 不搶,免得把使用者正在看的完成狀態蓋掉。§1.5 只回 PENDING/RUNNING 且只回自己建的,
   * 所以取消掉的批次不會陰魂不散。
   */
  useEffect(() => {
    if (useCheckBatchStore.getState().batch) return;
    let cancelled = false;
    void services.checkService
      .listActiveBatches(1)
      .then((jobs) => {
        // 期間使用者可能自己按了一發 → 再確認一次沒有 batch 才寫入
        if (cancelled || !jobs.length || useCheckBatchStore.getState().batch) return;
        useCheckBatchStore.getState().setBatch(jobs[0]);
      })
      .catch(() => {
        /* 冷啟還原是加分項,失敗就當沒有進行中的批次(fixture 版回空陣列不 reject) */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!batchId) {
      setFirestorePath('');
      setItemsPath('');
      return;
    }
    let cancelled = false;

    const snap = () => {
      lastSnapAtRef.current = Date.now();
      return services.checkService
        .getBatch(batchId)
        .then((status) => {
          if (cancelled) return;
          if (!status) {
            useCheckBatchStore.getState().clearBatch();
            setFirestorePath('');
            setItemsPath('');
            return;
          }
          const store = useCheckBatchStore.getState();
          // ⚠️ 同一個 batch 的補抓要用 **patch**:`listenerBlocked` 是 FE-only 旗標,
          // REST 快照不含它 → 整包 setBatch 會把它擦掉,而 Firestore listener 已因
          // permission-denied 終止、不會再報一次錯 → 手動更新出口就此消失(降級態
          // 從此沒有出口)。換 batch 才整包寫入。
          if (store.batch?.batchId === status.batchId) store.patchBatch(status);
          else store.setBatch(status);
          setFirestorePath(status.firestorePath ?? '');
          setItemsPath(status.itemsPath ?? '');
        })
        .catch(() => {
          /* 對帳失敗(暫時網路錯)→ 留給下次;fixture 版不 reject */
        });
    };
    void snap();

    const unsubscribe = services.checkService.subscribeBatch(batchId, (status) => {
      useCheckBatchStore.getState().setBatch(status);
    });

    // listener 拿不到權限時的補救:回到前景取一次快照(節流 5s)。不是輪詢 —
    // 沒有 timer,使用者不看這一頁就不會有請求。
    const onFocus = () => {
      if (document.visibilityState === 'hidden') return;
      if (Date.now() - lastSnapAtRef.current < 5_000) return;
      void snap();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);

    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [batchId]);

  /** job doc → 只套計數/進度(**沒有 items**,不可整包覆寫)。 */
  const onJobSnapshot = useCallback((raw: unknown) => {
    if (!raw) return;
    const patch = mapCheckJobDoc(raw as CheckJobDocDto);
    if (patch) useCheckBatchStore.getState().patchBatch(patch);
  }, []);

  /** items 子集合 → 只套逐件結果(results / reportIds / order)。 */
  const onItemsSnapshot = useCallback((raw: unknown) => {
    const docs = Array.isArray(raw) ? (raw as Array<CheckJobItemDto | null>) : null;
    if (!docs) return;
    const { results, reportIds, order } = mapCheckJobItemsDocs(docs);
    useCheckBatchStore.getState().patchBatch({
      results,
      reportIds,
      ...(order.length ? { order } : {}),
    });
  }, []);

  const onListenerError = useCallback((err: unknown) => {
    // listener 掛掉不該讓 minibar 消失(store 保留最後狀態);記一次供診斷。
    // permission-denied = rules 未部署(docs/87)→ 標記讓 UI 給手動更新出口。
    console.warn('[checkBatch] firestore listener error', err);
    const code = (err as { code?: string } | null)?.code;
    if (code === 'permission-denied') {
      useCheckBatchStore.getState().patchBatch({ listenerBlocked: true });
    }
  }, []);

  // path 為空字串時 hook 走 no-op 分支(fixture 期 / 尚未拿到 paths)
  useFirestoreSubscription<unknown>({
    path: firestorePath,
    type: 'doc',
    onSnapshot: onJobSnapshot,
    onError: onListenerError,
  });
  useFirestoreSubscription<unknown>({
    path: itemsPath,
    type: 'collection',
    onSnapshot: onItemsSnapshot,
    onError: onListenerError,
  });
}
